import React, { useCallback, useMemo, useState } from 'react'
import { createContext } from 'lib/createContext'
import {
  Loading,
  Message,
  SimpleMemoryRouterProvider,
  useIsRootRoute,
  useSimpleMemoryRouterActions,
  useSimpleMemoryRouterActiveRoutes,
  useSimpleMemoryRouterProviderContextValue,
} from '@workwhile/ui'
import { useCompaniesQuery } from 'queries/companies'

import { Companies } from 'pages/Companies/types'
import { useBulkCreateShiftsRouter } from '../useBulkCreateShiftsRouter'
import {
  useBulkLocationCheckMutation,
  useBulkLocationCreateMutation,
} from 'queries/shifts/bulkShiftCreate'
import {
  BulkLocationCheckResponse,
  BulkLocationCreateErrorResponse,
  BulkLocationCreateResponse,
  BulkShiftCreateErrorResponse,
  BulkShiftCreateResponse,
  CreateLocationSchema,
  CreateShiftSchema,
} from 'api/shifts'
import { useBulkShiftCreateMutation } from 'queries/shifts/bulkShiftCreate/useBulkShiftCreateMutation'
import { ShiftsTableSchema } from '../bulkCreateShiftsSchema'
import { format } from 'date-fns'

type BulkCreateShiftsContextState = {
  state: {
    activeRoute: ReturnType<typeof useBulkCreateShiftsRouter>['routes'][number]
    activeIndex: number
    allRoutes: ReturnType<typeof useBulkCreateShiftsRouter>['routes']
    companies: Companies
    formState: ShiftsTableSchema
    checkLocationsState: {
      isPending: boolean
      result: BulkLocationCheckResponse | undefined
      error: BulkLocationCreateErrorResponse | null
    }
    createLocationsState: {
      isPending: boolean
      result: BulkLocationCreateResponse | undefined
      error: BulkLocationCreateErrorResponse | null
    }
    createShiftsState: {
      isPending: boolean
      result: BulkShiftCreateResponse | undefined
    }
    createShiftsError: BulkShiftCreateErrorResponse | null
  }
  actions: {
    setFormState: React.Dispatch<React.SetStateAction<ShiftsTableSchema>>
    previous: {
      isAvailable: boolean
      onClick: () => void
    }
    reset: () => void
    checkLocations: (
      data: ShiftsTableSchema
    ) => Promise<BulkLocationCheckResponse | boolean>
    createLocationsAndShifts: (
      data: ShiftsTableSchema
    ) => Promise<BulkShiftCreateResponse | boolean>
    replace: (path: string) => void
  }
}

const [Context, useBulkCreateShiftsContextValue] =
  createContext<BulkCreateShiftsContextState>({
    name: 'BulkCreateShiftsContext',
  })

export const BulkCreateShiftsContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [formState, setFormState] = useState<ShiftsTableSchema>({
    shifts: [],
  })

  const {
    data: companies,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
  } = useCompaniesQuery()

  const {
    mutateAsync: bulkLocationCheck,
    isPending: isCheckingLocations,
    data: bulkLocationCheckResult,
    error: bulkLocationCheckError,
    reset: resetBulkLocationCheck,
  } = useBulkLocationCheckMutation()

  const {
    mutateAsync: bulkLocationCreate,
    isPending: isCreatingLocations,
    data: bulkLocationCreateResult,
    error: bulkLocationCreateError,
    reset: resetBulkLocationCreate,
  } = useBulkLocationCreateMutation()

  const {
    mutateAsync: bulkShiftCreate,
    isPending: isCreatingShifts,
    data: bulkShiftCreateResult,
    error: bulkShiftCreateError,
    reset: resetBulkShiftCreate,
  } = useBulkShiftCreateMutation()

  const { routes } = useSimpleMemoryRouterProviderContextValue()
  const { go, replace } = useSimpleMemoryRouterActions()
  const isRootRoute = useIsRootRoute()
  const [activeRoute] = useSimpleMemoryRouterActiveRoutes()
  const findRouteIndex = useCallback(
    (path: string) => {
      return routes.findIndex((route) => route.path === path)
    },
    [routes]
  )

  const activeIndex = useMemo(() => {
    return findRouteIndex(activeRoute?.path ?? '')
  }, [activeRoute?.path, findRouteIndex])

  if (!activeRoute) {
    throw new Error('No active route found')
  }

  const isAnyLoading = isCompaniesLoading

  const isAnyError = isCompaniesError

  if (isAnyLoading) {
    return <Loading />
  }

  if (isAnyError) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  const value = {
    state: {
      activeRoute,
      activeIndex,
      allRoutes: routes,
      companies: companies ?? [],
      formState,
      checkLocationsState: {
        isPending: isCheckingLocations,
        result: bulkLocationCheckResult,
        error: bulkLocationCheckError,
      },
      createLocationsState: {
        isPending: isCreatingLocations,
        result: bulkLocationCreateResult,
        error: bulkLocationCreateError,
      },
      createShiftsState: {
        isPending: isCreatingShifts,
        result: bulkShiftCreateResult,
      },
      createShiftsError: bulkShiftCreateError,
    },
    actions: {
      setFormState,
      reset: () => {
        setFormState({ shifts: [] })
        resetBulkLocationCheck()
        resetBulkLocationCreate()
        resetBulkShiftCreate()
      },
      previous: {
        isAvailable: !isRootRoute && activeIndex > 0,
        onClick: () => go(-1),
      },
      checkLocations: async (data: ShiftsTableSchema) => {
        const locations: CreateLocationSchema[] = data.shifts.map((shift) => ({
          rowId: shift.rowId,
          companyId: shift.companyId,
          locationName: shift.locationName,
          locationArrivalInstructions: shift.locationArrivalInstructions,
          locationStreet: shift.locationStreet,
          locationUnit: shift.locationUnit,
          locationCity: shift.locationCity,
          locationState: shift.locationState,
          locationZip: shift.locationZip,
        }))

        try {
          return await bulkLocationCheck({ locations })
        } catch (error) {
          return false
        }
      },
      createLocationsAndShifts: async (data: ShiftsTableSchema) => {
        try {
          const locations: CreateLocationSchema[] = data.shifts.map(
            (shift) => ({
              rowId: shift.rowId,
              companyId: shift.companyId,
              locationName: shift.locationName,
              locationArrivalInstructions: shift.locationArrivalInstructions,
              locationStreet: shift.locationStreet,
              locationUnit: shift.locationUnit,
              locationCity: shift.locationCity,
              locationState: shift.locationState,
              locationZip: shift.locationZip,
            })
          )

          const locationResult = await bulkLocationCreate({ locations })

          if (!locationResult?.locations) {
            return false
          }

          const shifts: CreateShiftSchema[] = data.shifts.map((shift) => ({
            rowId: shift.rowId,
            companyId: shift.companyId,
            locationId: locationResult.locations[shift.rowId].locationId,
            positionId: shift.positionId,
            shiftStartDate: format(
              shift.shiftStartDate,
              "yyyy-MM-dd'T'HH:mm:ss"
            ),
            shiftEndDate: format(shift.shiftEndDate, "yyyy-MM-dd'T'HH:mm:ss"),
            workersNeeded: shift.shiftWorkersNeeded,
            lunchLength: shift.shiftLunchLength,
            payRate: shift.shiftPayRate,
            payLumpSum: shift.shiftPayLumpSum,
            bundleGroup: shift.shiftBundleGroup,
            supervisorName: shift.shiftSupervisorName,
            supervisorPhone: shift.shiftSupervisorPhone,
            workerIds: shift.workerIds,
          }))

          return await bulkShiftCreate({ shifts })
        } catch (error) {
          return false
        }
      },
      replace,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useBulkCreateShiftsContextValue }

export const BulkCreateShiftsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const router = useBulkCreateShiftsRouter()

  return (
    <SimpleMemoryRouterProvider router={router}>
      <BulkCreateShiftsContextProvider>
        {children}
      </BulkCreateShiftsContextProvider>
    </SimpleMemoryRouterProvider>
  )
}
